import { useState } from 'react';
import { Journals } from '../../../types/Journals.types';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  Typography,
  Stack,
  Divider,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { mapAgreementStatus } from '../util/agreement-status';
import { FilterPopover } from './FilterPopover';
import { InternalSearchFilter, InternalSearchFilterKeys } from '../types';

interface JournalsTableMobileProps {
  journals: Journals[] | undefined;
  filters: InternalSearchFilter;
  selectedFilters: InternalSearchFilter;
  hasFiltersApplied?: boolean;
  onToggleFilter: (type: InternalSearchFilterKeys, value: string) => void;
}

export const JournalsTableMobile = ({
  journals,
  filters,
  selectedFilters,
  hasFiltersApplied = false,
  onToggleFilter,
}: JournalsTableMobileProps) => {
  const { t } = useTranslation();
  const [openFilter, setOpenFilter] = useState<{
    key: InternalSearchFilterKeys;
    anchor: HTMLElement;
  } | null>(null);

  const hasJournals = journals && journals.length > 0;

  return (
    <Stack spacing={2} sx={{ mt: 3 }}>
      {(hasJournals || hasFiltersApplied) && (
        <Card
          variant="default"
          aria-label="Journals filters"
          sx={{
            p: 24,
            my: 16,
            backgroundColor: 'var(--color-scifree-teal)',
          }}
        >
          <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 16 }}>
            <FilterPopover
              label={t('agreements.imprint')}
              options={filters.imprints}
              selectedOptions={selectedFilters.imprints}
              onToggle={(imprint) => onToggleFilter('imprints', imprint)}
              anchorEl={
                openFilter?.key === 'imprints' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'imprints'}
              onOpen={(e) =>
                setOpenFilter({ key: 'imprints', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
            />
            <FilterPopover
              label={t('agreements.createFlow.payment')}
              options={filters.payments}
              selectedOptions={selectedFilters.payments}
              onToggle={(payment) => onToggleFilter('payments', payment)}
              anchorEl={
                openFilter?.key === 'payments' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'payments'}
              onOpen={(e) =>
                setOpenFilter({ key: 'payments', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
            />
            <FilterPopover
              label={t('agreements.licenseOption')}
              options={filters.licenses}
              selectedOptions={selectedFilters.licenses}
              onToggle={(license) => onToggleFilter('licenses', license)}
              anchorEl={
                openFilter?.key === 'licenses' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'licenses'}
              onOpen={(e) =>
                setOpenFilter({ key: 'licenses', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
            />

            <FilterPopover
              label={t('agreements.publishingModel')}
              options={filters.publishingModels}
              selectedOptions={selectedFilters.publishingModels}
              onToggle={(model) => onToggleFilter('publishingModels', model)}
              anchorEl={
                openFilter?.key === 'publishingModels'
                  ? openFilter.anchor
                  : null
              }
              open={openFilter?.key === 'publishingModels'}
              onOpen={(e) =>
                setOpenFilter({
                  key: 'publishingModels',
                  anchor: e.currentTarget,
                })
              }
              onClose={() => setOpenFilter(null)}
            />
            <FilterPopover
              label={t('agreements.status')}
              options={filters.statuses}
              selectedOptions={selectedFilters.statuses}
              onToggle={(status) => onToggleFilter('statuses', status)}
              anchorEl={
                openFilter?.key === 'statuses' ? openFilter.anchor : null
              }
              open={openFilter?.key === 'statuses'}
              onOpen={(e) =>
                setOpenFilter({ key: 'statuses', anchor: e.currentTarget })
              }
              onClose={() => setOpenFilter(null)}
              optionLabelMapper={mapAgreementStatus}
            />
          </Stack>
        </Card>
      )}
      <Stack spacing={12} sx={{ mt: 2 }}>
        {journals?.map((journal, index) => (
          <Card
            variant="primary"
            key={
              journal._id ||
              `journal-card-${journal.url}-${journal.name}-${
                journal.agreement_id || index
              }`
            }
            sx={{ '&:hover': { cursor: 'default' } }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption">
                  {t('agreements.journal')}
                </Typography>
                <Typography variant="body1">
                  <MuiLink
                    href={journal.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {journal.name}
                  </MuiLink>
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.imprint')}
                </Typography>
                <Typography variant="body1">
                  {journal.imprint || '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.createFlow.payment')}
                </Typography>
                <Typography variant="body1">
                  {journal.payment || '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.licenseOption')}
                </Typography>
                <Typography variant="body1">
                  {journal.licenses?.join(', ') || '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.publishingModel')}
                </Typography>
                <Typography variant="body1">
                  {journal.publishing_model || '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.issne')}
                </Typography>
                <Typography variant="body1">{journal.issn_e || '-'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.issnp')}
                </Typography>
                <Typography variant="body1">{journal.issn_p || '-'}</Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.subject')}
                </Typography>
                <Typography variant="body1">
                  {journal.subjects || '-'}
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.agreement')}
                </Typography>
                <Typography variant="body1">
                  <MuiLink
                    component={RouterLink}
                    to={`/agreements/${journal.agreement_id}`}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {journal.agreement_name}
                  </MuiLink>
                </Typography>
              </Box>
              <Divider />
              <Box>
                <Typography variant="caption">
                  {t('agreements.status')}
                </Typography>
                <Typography variant="body1" component="div">
                  {journal.status ? (
                    <Chip
                      size="small"
                      variant={
                        journal.status === 'enabled' ? 'success' : 'secondary'
                      }
                      label={mapAgreementStatus(journal.status)}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  ) : (
                    '-'
                  )}
                </Typography>
              </Box>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
};
